import { Suspense, lazy } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import CookieConsentAlert from '../components/CookieConsentAlert';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Loading from '../components/loading';
import useNotifier from '../components/useNotifier';
import { UserRoles } from '../constants/roles';
import NotFoundPage from '../pages/NotFoundPage';
import SALPRedirect from '../pages/SALPRedirect';
import VerifyNotaryPage from '../pages/VerifyNotaryPage';
import OrderDetailsRouter from './OrderDetailsRouter';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const IpenNotaryProfilePage = lazy(
  () => import('../pages/IpenNotaryProfilePage')
);
const CreateOrderPage = lazy(() => import('../pages/CreateOrderPage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const CallBackPage = lazy(() => import('../pages/CallBackPage'));
const OrdersPage = lazy(() => import('../pages/OrdersPage'));
const AdminDashboardPage = lazy(() => import('../pages/AdminDashboardPage'));
const ViewFilePage = lazy(() => import('../pages/ViewFilePage'));

const AppRouter = () => {
  useNotifier();

  return (
    <Suspense
      fallback={
        <div className='full-page-loader'>
          <Loading showLogo text='Loading...' />
        </div>
      }>
      <CookieConsentAlert />
      <BrowserRouter>
        <Routes>
          <Route
            path='/'
            element={
              <PublicRoute>
                <LoginPage />
                <Footer />
              </PublicRoute>
            }
          />

          <Route path='/salp/:sessionid/:partyid' element={<SALPRedirect />} />

          <Route
            path='/logout'
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          <Route
            path='/login/callback'
            element={
              <PublicRoute>
                <CallBackPage />
              </PublicRoute>
            }
          />
          <Route
            path='/create'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.EOEA_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <CreateOrderPage />
              </PrivateRoute>
            }
          />

          <Route
            path='/create/:id'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.EOEA_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <CreateOrderPage />
              </PrivateRoute>
            }
          />

          <Route
            path='/orders'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.EOEA_USERS_ROLE,
                  UserRoles.NOTARY_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <OrdersPage />
              </PrivateRoute>
            }
          />

          <Route
            path='/orders/:id'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.EOEA_USERS_ROLE,
                  UserRoles.NOTARY_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                  UserRoles.GUEST_USERS_ROLE,
                ]}>
                <Header />
                <OrderDetailsRouter />
              </PrivateRoute>
            }
          />

          <Route
            path='/admin'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.EOEA_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <AdminDashboardPage />
              </PrivateRoute>
            }
          />

          <Route
            path='/notary/registration'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.NOTARY_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <IpenNotaryProfilePage />
              </PrivateRoute>
            }
          />

          <Route
            path='/verify-profile'
            element={
              <PrivateRoute
                roles={[
                  UserRoles.NOTARY_USERS_ROLE,
                  UserRoles.GLOBAL_USERS_ROLE,
                ]}>
                <Header />
                <VerifyNotaryPage />
              </PrivateRoute>
            }
          />

          <Route
            path='/view-file'
            element={
              <PrivateRoute roles={[UserRoles.ALL]}>
                <ViewFilePage />
              </PrivateRoute>
            }
          />

          <Route path='*' element={<NotFoundPage />} />
          <Route path='/not-found' element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

export default AppRouter;
